import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ChatChannel } from '@app/model/chat-channel';
import { SendbirdService } from '@app/services/sendbird.service';
import { MOBILE_CHAT_DIALOG_TOP_GAP } from '@app/shared/constants/chat-dialog.const';
import { VirtualKeyboardService } from '@app/services/virtual-keyboard.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatMode } from 'src/app/model/enums/chat-mode.enum';
import { ChatChannelComponent } from '../chat-channel/chat-channel.component';

@Component({
    selector: 'app-chat-dialog',
    templateUrl: './chat-dialog.component.html',
    styleUrls: ['./chat-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatDialogComponent implements OnInit, OnDestroy {
  @ViewChild(ChatChannelComponent) chatChannelComponent: ChatChannelComponent;
  public mode = ChatMode.DIALOG;

  public groupChannel: ChatChannel;

  public collapsed = false;

  private readonly unsubscribe: Subject<void> = new Subject();

  private readonly desktopMediaQuery = window.matchMedia('(min-width: 992px)');

  constructor(
    private readonly sendbirdService: SendbirdService,
    private readonly virtualKeyboardService: VirtualKeyboardService,
    private readonly dialogRef: MatDialogRef<ChatDialogComponent>) { }

  ngOnInit() {
    this.sendbirdService.activeChannel
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => this.groupChannel = data);

    this.followVisibleViewport();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public changeDialogState(): void {
    this.collapsed = !this.collapsed;
    this.sendbirdService.minimizeChatDialogToggle(this.collapsed);
    this.chatChannelComponent?.chatInput?.closeRecordDialog();

    if (!this.collapsed) {
      this.sendbirdService.markChannelAsRead(this.groupChannel);
    }
  }

  public collapseDialog(): void {
    if (this.collapsed) {
      return;
    }

    this.collapsed = true;
    this.sendbirdService.minimizeChatDialogToggle(this.collapsed);
  }

  public closeDialog(): void {
    this.sendbirdService.closeChatDialog();
  }

  private followVisibleViewport(): void {
    if (this.desktopMediaQuery.matches) {
      return;
    }

    this.virtualKeyboardService.observe()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(({ height, bottomInset }) => {
        this.dialogRef.updateSize('100vw', `calc(${height}px - ${MOBILE_CHAT_DIALOG_TOP_GAP}px - env(safe-area-inset-top))`);
        this.dialogRef.updatePosition({ bottom: `${bottomInset}px`, left: '0px' });

        // The pane needs a frame to reflow before the scroll offset can be re-pinned.
        requestAnimationFrame(() => this.chatChannelComponent?.restoreScrollPosition());
      });
  }

}
